import type React from 'react';
import { forwardRef } from 'react';
import { Link, type LinkProps } from 'react-router';

type HashLinkProps = Omit<LinkProps, 'to'> & {
    elementId?: string;
    scroll?: (element: HTMLElement) => void;
    smooth?: boolean;
    timeout?: number;
    to: LinkProps['to'];
};

let hashFragment = '';
let observer: MutationObserver | null = null;
let asyncTimerId: number | null = null;
let scrollFunction: ((element: HTMLElement) => void) | null = null;

const reset = () => {
    hashFragment = '';
    observer?.disconnect();
    observer = null;

    if (asyncTimerId !== null) {
        window.clearTimeout(asyncTimerId);
        asyncTimerId = null;
    }
};

const isInteractiveElement = (element: HTMLElement) => {
    const formTags = ['BUTTON', 'INPUT', 'SELECT', 'TEXTAREA'];
    const linkTags = ['A', 'AREA'];

    return (
        (formTags.includes(element.tagName) && !element.hasAttribute('disabled')) ||
        (linkTags.includes(element.tagName) && element.hasAttribute('href'))
    );
};

const getHashFromTo = (to: LinkProps['to']) => {
    if (typeof to === 'string' && to.includes('#')) {
        return `#${to.split('#').slice(1).join('#')}`;
    }

    if (typeof to === 'object' && typeof to.hash === 'string') {
        return to.hash;
    }

    return '';
};

const getElementAndScroll = () => {
    const element =
        hashFragment === '#'
            ? document.body
            : (document.getElementById(hashFragment.replace('#', '')) ??
              (hashFragment === '#top' ? document.body : null));

    if (!element || !scrollFunction) {
        return false;
    }

    scrollFunction(element);

    const originalTabIndex = element.getAttribute('tabindex');
    if (originalTabIndex === null && !isInteractiveElement(element)) {
        element.setAttribute('tabindex', '-1');
    }

    element.focus({ preventScroll: true });

    if (originalTabIndex === null && !isInteractiveElement(element)) {
        element.blur();
        element.removeAttribute('tabindex');
    }

    reset();
    return true;
};

const scrollToHashLink = (timeout?: number) => {
    window.setTimeout(() => {
        if (getElementAndScroll()) {
            return;
        }

        observer = new MutationObserver(getElementAndScroll);
        observer.observe(document, {
            attributes: true,
            childList: true,
            subtree: true,
        });

        asyncTimerId = window.setTimeout(reset, timeout ?? 10000);
    }, 0);
};

export const HashLink = forwardRef<HTMLAnchorElement, HashLinkProps>((props, ref) => {
    const { elementId, onClick, scroll, smooth, timeout, to, ...linkProps } = props;
    const linkHash = getHashFromTo(to);

    const handleClick: React.MouseEventHandler<HTMLAnchorElement> = event => {
        reset();
        hashFragment = elementId ? `#${elementId}` : linkHash;
        onClick?.(event);

        if (
            hashFragment &&
            !event.defaultPrevented &&
            event.button === 0 &&
            (!props.target || props.target === '_self') &&
            !(event.metaKey || event.altKey || event.ctrlKey || event.shiftKey)
        ) {
            scrollFunction = scroll ?? (element => element.scrollIntoView(smooth ? { behavior: 'smooth' } : undefined));
            scrollToHashLink(timeout);
        }
    };

    return <Link {...linkProps} to={to} onClick={handleClick} ref={ref} />;
});

HashLink.displayName = 'HashLink';
